
import { forwardRef, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import ReactDOMServer from "react-dom/server";
import { ToastContainer } from "react-toastify";
import Loader from "../../Loader";
import { renderDangerousHTML } from "../../CustomFunctions";
import { formatContentForPrint, getImageSource, getPaperDimensions, parsePadding } from "../../CustomUtilities";
import { IMAGE_URL } from "../../../config/axios";

const LetterPreview = forwardRef(({ letterTemplateData, buffer1, buffer2, buffer3, children, letterData, contentBorderDisplay, subCompany, showDefaultSignText = false, lineHeight }, ref) => {
    const [pages, setPages] = useState([]);
    const [isClient, setIsClient] = useState(false);
    useEffect(() => {
        setIsClient(true);
    }, []);


    const footerRef = useRef(null);
    const [footerHeightPx, setFooterHeightPx] = useState(0);
    const headerRef = useRef(null);
    const [headerHeightPx, setHeaderHeightPx] = useState(0);


    useLayoutEffect(() => {
        if (!footerRef.current || !headerRef.current) return;
        const headerElement = headerRef.current;
        const footerElement = footerRef.current;

        const updateHeights = () => {
            if (headerElement) {
                setHeaderHeightPx(headerElement.offsetHeight);
            }
            if (footerElement) {
                setFooterHeightPx(footerElement.offsetHeight);
            }
        };

        updateHeights();
        const resizeObserver = new ResizeObserver(() => {
            updateHeights();
        });

        if (headerElement) resizeObserver.observe(headerElement);
        if (footerElement) resizeObserver.observe(footerElement);

        return () => {
            resizeObserver.disconnect();
        };
    }, [
        letterTemplateData,
        // fontSize,
        letterTemplateData?.page_size
    ]);

    const getJustifyContent = (letterTemplateData, type = "header") => {
        if (!letterTemplateData) return "space-between";
        const positionPrefix = type === "header" ? "header_" : "footer_";

        const logo_position = letterTemplateData[`${positionPrefix}logo_position`];
        const content_position =
            letterTemplateData[`${positionPrefix}content_position`];

        if (logo_position === "left" && content_position === "left")
            return "flex-start";
        if (logo_position === "right" && content_position === "right")
            return "flex-end";
        if (logo_position === "center" && content_position === "right")
            return "flex-end";
        if (logo_position === "right" && content_position === "center")
            return "flex-end";
        if (logo_position === "left" && content_position === "center")
            return "start";
        if (logo_position === "center" && content_position === "left") return "start";
        if (logo_position === "center" && content_position === "center")
            return "center";

        return "space-between";
    };


    const getJustifyContentMore = (letterTemplateData, type = "header") => {
        if (!letterTemplateData) return "flex-start";
        const positionPrefix = type === "header" ? "header_" : "footer_";

        const logo = letterTemplateData[`${positionPrefix}logo_position`];
        const content = letterTemplateData[`${positionPrefix}content_position`];

        if (logo === "center" || content === "center") return "center";
        if (logo === "right" || content === "right") return "flex-end";

        return "flex-start";
    };
    const mmToPx = (mm) => (parseFloat(mm) / 25.4) * 96;


    const getPageLayout = (pageSize, header, footer) => {
        const pageDimensions = getPaperDimensions(pageSize);
        const pageHeightPx = mmToPx(pageDimensions.height);
        const pageWidthPx = mmToPx(pageDimensions.width);
        // fontSize = fontSize ?? 13

        const paddingTop = parsePadding(letterTemplateData?.header_bottom_gap);
        const paddingBottom = parsePadding(letterTemplateData?.footer_top_gap);
        const paddingLeft = parsePadding(letterTemplateData?.content_left_gap) || 20;
        const paddingRight = parsePadding(letterTemplateData?.content_right_gap) || 20;

        // FORCING the math to reserve 200px+ for the header and 250px+ for the footer
        const headerHeightToUse = header ? (headerHeightPx || (letterTemplateData?.header_full_image ? 200 : 180)) : 20;
        const footerHeightToUse = footer ? (footerHeightPx || (letterTemplateData?.footer_full_image ? 250 : 250)) : 20;

        const usableHeight =
            pageHeightPx -
            headerHeightToUse -
            footerHeightToUse -
            paddingTop -
            paddingBottom;
        const usableWidth = pageWidthPx - paddingLeft - paddingRight;

        return {
            pageDimensions,
            pageHeightPx,
            pageWidthPx,
            footerHeightPx,
            headerHeightPx,
            // headerHeight,
            // footerHeight,
            paddingBottom,
            paddingLeft,
            paddingRight,
            paddingTop,
            usableHeight,
            usableWidth,
        };
    };

    const getBgImageStyle = () => {
        if (!letterTemplateData.bg_image) return {};

        const imageUrl = getImageSource(letterTemplateData?.bg_image);
        return {
            backgroundImage: `url(${imageUrl})`,
            backgroundSize: `${letterTemplateData.bg_size || 100}%`,
            transform: letterTemplateData.bg_angle
                ? `rotate(${letterTemplateData.bg_angle}deg)`
                : "none",
            backgroundRepeat: 'no-repeat',
            opacity: (letterTemplateData?.bg_opacity || 100) / 100,
        };
    };



    const splitHtmlContentIntoPages = (inputContent, pageSize, header, footer) => {
        if (!isClient) return [];

        const layout = getPageLayout(pageSize, header, footer);

        let htmlContent = formatContentForPrint(inputContent || "");

        if (letterTemplateData?.signatory_display || showDefaultSignText) {
            const sigHtml = ReactDOMServer.renderToStaticMarkup(
                <div
                    className="unbreakable-signature"
                    style={{
                        marginTop: letterTemplateData?.signatory_overall_placement === "End" ? "30px" : "0px",
                        marginBottom: letterTemplateData?.signatory_overall_placement === "Start" ? "30px" : "0px",
                        breakInside: "avoid"
                    }}
                >
                    <SignatureBlock letterTemplateData={letterTemplateData} showDefaultSignText={showDefaultSignText} letterData={letterData} subCompany={subCompany} />
                </div>
            );

            if (letterTemplateData?.signatory_overall_placement === "Start") {
                htmlContent = sigHtml + htmlContent;
            } else {
                htmlContent = htmlContent + sigHtml;
            }
        }

        const tempContainer = document.createElement("div");
        tempContainer.className = "letter-template-content-placeholder-e letter-template-body-e-e";
        tempContainer.style.position = "absolute";
        tempContainer.style.visibility = "hidden";
        tempContainer.style.left = "-9999px";
        tempContainer.style.top = "0";
        tempContainer.style.width = layout.pageWidthPx + "px";
        tempContainer.style.paddingLeft = layout.paddingLeft + "px";
        tempContainer.style.paddingRight = layout.paddingRight + "px";
        tempContainer.style.paddingTop = "0px";
        tempContainer.style.paddingBottom = "0px";
        tempContainer.style.lineHeight = lineHeight;
        tempContainer.style.fontFamily = "'Poppins', sans-serif";
        tempContainer.style.boxSizing = "border-box";
        tempContainer.style.wordWrap = "break-word";
        tempContainer.style.overflowWrap = "break-word";
        tempContainer.style.whiteSpace = "normal";
        tempContainer.style.margin = "0";
        tempContainer.style.border = "none";
        tempContainer.style.backgroundColor = "white";
        tempContainer.style.color = "#666";
        tempContainer.style.overflow = "hidden";
        tempContainer.style.height = "auto";

        document.body.appendChild(tempContainer);

        const pages = [];
        let currentPageHtml = "";

        const parser = new DOMParser();
        const doc = parser.parseFromString(htmlContent, "text/html");
        let blocks = [];

        // Better block preservation
        Array.from(doc.body.childNodes).forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
                if (node.classList?.contains("salaryslip-body")) {
                    Array.from(node.children).forEach(child => {
                        if (child.nodeName === "TABLE") {
                            blocks.push(child);
                        } else {
                            blocks.push(child);
                        }
                    });
                } else {
                    blocks.push(node);
                }
            } else if (node.nodeType === Node.TEXT_NODE && node.textContent.trim()) {
                blocks.push(node);
            }
        });

        // Helper: calculate total margins and padding (still used for tables)
        const calculateElementExtraSpace = (element) => {
            if (!element || element.nodeType !== Node.ELEMENT_NODE) return 0;
            const styles = window.getComputedStyle(element);
            const marginTop = parseInt(styles.marginTop) || 0;
            const marginBottom = parseInt(styles.marginBottom) || 0;
            const paddingTop = parseInt(styles.paddingTop) || 0;
            const paddingBottom = parseInt(styles.paddingBottom) || 0;
            return marginTop + marginBottom + paddingTop + paddingBottom;
        };

        // Measure real-time footer height as safety net
        let measuredFooterH = 0;
        if (footer && footerRef?.current) {
            const rect = footerRef.current.getBoundingClientRect();
            measuredFooterH = rect.height || 0;
        }
        const footerSafetyDelta = Math.max(0, measuredFooterH - footerHeightPx);

        // --------------------------------------------------------------
        // DYNAMIC MAX HEIGHT CALCULATION (no fixed buffer)
        // --------------------------------------------------------------
        const MIN_SAFETY_MARGIN = 40;
        let maxHeight = layout.usableHeight - footerSafetyDelta - MIN_SAFETY_MARGIN;

        if (letterTemplateData?.signatory_overall_placement === "Start") {
            maxHeight += 20;
        } else if (letterTemplateData?.signatory_overall_placement === "End" &&
            (letterTemplateData?.signatory_display || showDefaultSignText)) {
            maxHeight -= 0;  // Removed arbitrary penalty
        }

        // Function to split table rows across pages
        const splitTable = (table, currentPageHtml, maxHeight, isFirstTableAppearance = true) => {
            const result = { html: '', remaining: null };
            const tableClone = table.cloneNode(true);
            const allRows = Array.from(tableClone.querySelectorAll('tr'));
            if (allRows.length === 0) return result;

            const thead = tableClone.querySelector('thead');
            const theadRows = thead ? Array.from(thead.querySelectorAll('tr')) : [];
            const tbody = tableClone.querySelector('tbody');
            const tbodyRows = tbody ? Array.from(tbody.querySelectorAll('tr')) : allRows.filter(row => !theadRows.includes(row));

            const tableAttrs = {};
            Array.from(table.attributes).forEach(attr => { tableAttrs[attr.name] = attr.value; });

            let rowsToKeep = [];
            let rowsToMove = [];
            const shouldIncludeThead = (isFirstTableAppearance || currentPageHtml === "") && theadRows.length > 0;

            // Test rows one by one
            for (let i = 0; i < tbodyRows.length; i++) {
                const testRows = rowsToKeep.concat(tbodyRows[i]);
                const attrsString = Object.entries(tableAttrs).map(([k, v]) => `${k}="${v}"`).join(' ');
                let testTableHtml = `<table ${attrsString}>`;
                if (shouldIncludeThead && rowsToKeep.length === 0) {
                    testTableHtml += `<thead>${theadRows.map(row => row.outerHTML).join('')}</thead>`;
                }
                testTableHtml += `<tbody>${testRows.map(row => row.outerHTML).join('')}</tbody></table>`;
                tempContainer.innerHTML = currentPageHtml + testTableHtml;
                if (tempContainer.scrollHeight <= maxHeight) {
                    rowsToKeep.push(tbodyRows[i]);
                } else {
                    rowsToMove = tbodyRows.slice(i);
                    break;
                }
            }

            if (rowsToKeep.length === 0 && tbodyRows.length > 0) { if (currentPageHtml.trim() === "") { rowsToKeep.push(tbodyRows[0]); rowsToMove = tbodyRows.slice(1); } else { rowsToMove = tbodyRows; } }

            if (rowsToKeep.length > 0) {
                const attrsString = Object.entries(tableAttrs).map(([k, v]) => `${k}="${v}"`).join(' ');
                let tableHtml = `<table ${attrsString}>`;
                if (shouldIncludeThead && rowsToKeep.length > 0) {
                    tableHtml += `<thead>${theadRows.map(row => row.outerHTML).join('')}</thead>`;
                }
                tableHtml += `<tbody>${rowsToKeep.map(row => row.outerHTML).join('')}</tbody></table>`;
                result.html = tableHtml;
            }

            if (rowsToMove.length > 0) {
                const remainingTable = document.createElement('table');
                Object.entries(tableAttrs).forEach(([k, v]) => remainingTable.setAttribute(k, v));
                if (theadRows.length > 0) {
                    const newThead = document.createElement('thead');
                    theadRows.forEach(row => newThead.appendChild(row.cloneNode(true)));
                    remainingTable.appendChild(newThead);
                }
                const tbody = document.createElement('tbody');
                rowsToMove.forEach(row => tbody.appendChild(row.cloneNode(true)));
                remainingTable.appendChild(tbody);
                result.remaining = remainingTable;
            }
            return result;
        };

        // Process blocks one by one
        for (let i = 0; i < blocks.length; i++) {
            const block = blocks[i];

            // ---------- TEXT NODE ----------
            if (block.nodeType === Node.TEXT_NODE) {
                const text = block.textContent;
                if (!text.trim()) continue;

                const tempDiv = document.createElement('div');
                tempDiv.textContent = text;

                const blockHtml = tempDiv.outerHTML;

                tempContainer.innerHTML = currentPageHtml + blockHtml;
                const totalHeight = tempContainer.scrollHeight;  // no extra space addition

                if (totalHeight <= maxHeight) {
                    currentPageHtml += blockHtml;
                } else {
                    if (currentPageHtml) {
                        pages.push(currentPageHtml);
                        currentPageHtml = blockHtml;
                    } else {
                        // Split the text block into smaller chunks
                        const words = text.trim().split(/\s+/);
                        let start = 0;
                        while (start < words.length) {
                            let left = 1, right = words.length - start, best = 0;
                            while (left <= right) {
                                const mid = Math.floor((left + right) / 2);
                                const testText = words.slice(start, start + mid).join(" ");
                                tempDiv.innerHTML = testText;
                                tempContainer.innerHTML = currentPageHtml + tempDiv.outerHTML;
                                const testHeight = tempContainer.scrollHeight;
                                if (testHeight <= maxHeight) {
                                    best = mid;
                                    left = mid + 1;
                                } else {
                                    right = mid - 1;
                                }
                            }
                            if (best === 0) {
                                const wordDiv = document.createElement('div');
                                wordDiv.textContent = words[start];

                                currentPageHtml += wordDiv.outerHTML;
                                start++;
                            } else {
                                const wordDiv = document.createElement('div');
                                wordDiv.textContent = words.slice(start, start + best).join(" ");

                                currentPageHtml += wordDiv.outerHTML;
                                start += best;
                            }
                            if (start < words.length) {
                                pages.push(currentPageHtml);
                                currentPageHtml = "";
                            }
                        }
                    }
                }
                continue;
            }

            // ---------- TABLE ----------
            if (block.nodeType === Node.ELEMENT_NODE && block.nodeName === 'TABLE') {
                const tableResult = splitTable(block, currentPageHtml, maxHeight, true);
                if (tableResult.html) currentPageHtml += tableResult.html;
                if (tableResult.remaining) {
                    if (currentPageHtml.trim()) pages.push(currentPageHtml);
                    currentPageHtml = '';
                    blocks.splice(i + 1, 0, tableResult.remaining);
                }
                continue;
            }

            // ---------- DIV (with or without tables) ----------
            if (block.nodeType === Node.ELEMENT_NODE && block.nodeName === 'DIV') {
                const tables = block.querySelectorAll('table');
                if (tables.length > 0) {
                    const children = Array.from(block.childNodes);
                    let tempDiv = document.createElement('div');
                    Array.from(block.attributes).forEach(attr => tempDiv.setAttribute(attr.name, attr.value));
                    tempDiv.style.cssText = block.style.cssText;
                    let firstTableProcessed = false;

                    for (let child of children) {
                        if (child.nodeType === Node.ELEMENT_NODE && child.nodeName === 'TABLE') {
                            if (tempDiv.children.length > 0) {
                                const divHtml = tempDiv.outerHTML;
                                tempContainer.innerHTML = currentPageHtml + divHtml;
                                const totalHeight = tempContainer.scrollHeight;
                                if (totalHeight <= maxHeight) {
                                    currentPageHtml += divHtml;
                                } else {
                                    pages.push(currentPageHtml);
                                    currentPageHtml = divHtml;
                                }
                                tempDiv = document.createElement('div');
                                Array.from(block.attributes).forEach(attr => tempDiv.setAttribute(attr.name, attr.value));
                                tempDiv.style.cssText = block.style.cssText;
                            }
                            const tableResult = splitTable(child, currentPageHtml, maxHeight, !firstTableProcessed);
                            firstTableProcessed = true;
                            if (tableResult.html) currentPageHtml += tableResult.html;
                            if (tableResult.remaining) {
                                if (currentPageHtml.trim()) pages.push(currentPageHtml);
                                currentPageHtml = '';
                                const remainingDiv = block.cloneNode(false);
                                remainingDiv.appendChild(tableResult.remaining);
                                const remainingChildren = Array.from(children).slice(children.indexOf(child) + 1);
                                remainingChildren.forEach(c => remainingDiv.appendChild(c.cloneNode(true)));
                                blocks.splice(i + 1, 0, remainingDiv);
                                break;
                            }
                        } else {
                            tempDiv.appendChild(child.cloneNode(true));
                        }
                    }
                    if (tempDiv.children.length > 0) {
                        const divHtml = tempDiv.outerHTML;
                        tempContainer.innerHTML = currentPageHtml + divHtml;
                        const totalHeight = tempContainer.scrollHeight;
                        if (totalHeight <= maxHeight) {
                            currentPageHtml += divHtml;
                        } else {
                            pages.push(currentPageHtml);
                            currentPageHtml = divHtml;
                        }
                    }
                } else {
                    // Regular DIV without tables
                    const blockHtml = block.outerHTML;
                    tempContainer.innerHTML = currentPageHtml + blockHtml;
                    const totalHeight = tempContainer.scrollHeight;  // no extra space
                    if (totalHeight <= maxHeight) {
                        currentPageHtml += blockHtml;
                    } else {
                        if (currentPageHtml.trim() !== "") {
                            pages.push(currentPageHtml);
                            currentPageHtml = "";
                            i--; // retry this block on next page
                            continue;
                        } else {
                            pages.push(blockHtml);
                            currentPageHtml = "";
                        }
                    }
                }
                continue;
            }

            // ---------- Other ELEMENT_NODES (p, h1, etc.) ----------
            if (block.nodeType === Node.ELEMENT_NODE) {
                const blockHtml = block.outerHTML;
                tempContainer.innerHTML = currentPageHtml + blockHtml;
                const totalHeight = tempContainer.scrollHeight;
                if (totalHeight <= maxHeight) {
                    currentPageHtml += blockHtml;
                } else {
                    if (currentPageHtml.trim() !== "") {
                        pages.push(currentPageHtml);
                        currentPageHtml = "";
                        i--; // retry this block on next page
                        continue;
                    } else {
                        pages.push(blockHtml);
                        currentPageHtml = "";
                    }
                }
                continue;
            }
        }

        // Add remaining content
        if (currentPageHtml && currentPageHtml.trim() !== "") {
            pages.push(currentPageHtml);
        }

        document.body.removeChild(tempContainer);
        return pages.filter(p => p && p.trim() !== "");
    };

    const finalContent = useMemo(() => {
        if (typeof children === "string") {
            return children;
        }
        if (children) {
            return ReactDOMServer.renderToStaticMarkup(children);
        }
        return "";
    }, [children]);

    useEffect(() => {
        if (!isClient) return;

        const pageSizeToUse = letterTemplateData?.page_size || "A4";

        const contentPages = splitHtmlContentIntoPages(
            finalContent,
            pageSizeToUse,
            // fontSize,
            letterTemplateData?.header_display,
            letterTemplateData?.footer_display,
        );

        setPages(contentPages);

    }, [
        finalContent,
        letterTemplateData?.page_size,
        letterTemplateData?.header_display,
        letterTemplateData?.footer_display,
        // fontSize,
        isClient,
        headerHeightPx,
        footerHeightPx,

    ]);

    useEffect(() => {
        if (footerRef.current) {
            setFooterHeightPx(footerRef.current.offsetHeight);
        }
        if (headerRef.current) {
            setHeaderHeightPx(headerRef.current.offsetHeight);
        }
    }, [letterTemplateData?.header_full_image, letterTemplateData?.header_logo, letterTemplateData?.footer_full_image, letterTemplateData?.footer_logo]);


    const layout = getPageLayout(
        letterTemplateData?.page_size,
        // fontSize,
        letterTemplateData?.header_display,
        letterTemplateData?.footer_display
    );
    return (
        <>
            <ToastContainer position="top-center" autoClose={1000} hideProgressBar={false} newestOnTop={true} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />

            <div
                className="letter-template-container-e-e"
                style={{
                    padding: "0px",
                    overflow: "visible",
                    position: "relative",
                    width: "100%",
                }}
                ref={ref}
            >

                {buffer1 || buffer2 || buffer3 ? (
                    <Loader container={true} />
                ) : (
                    (pages.length > 0 ? pages : [null]).map((content, index) => {

                        return (
                            <div
                                key={index}
                                className={`letter-page-e-e ${letterTemplateData?.header_display || letterTemplateData?.footer_display
                                    ? "has-header-footer"
                                    : "no-header-footer"
                                    }`}
                                style={{
                                    // 1. FORCE EXACT PHYSICAL DIMENSIONS
                                    width: letterTemplateData?.page_size === "letter" ? "8.5in" : letterTemplateData?.page_size === "A3" ? "297mm" : "210mm",
                                    height: letterTemplateData?.page_size === "letter" ? "11in" : letterTemplateData?.page_size === "A3" ? "420mm" : "297mm",
                                    minWidth: letterTemplateData?.page_size === "letter" ? "8.5in" : letterTemplateData?.page_size === "A3" ? "297mm" : "210mm",
                                    minHeight: letterTemplateData?.page_size === "letter" ? "11in" : letterTemplateData?.page_size === "A3" ? "420mm" : "297mm",

                                    // 2. Center on screen like a real document editor
                                    margin: "0 auto 20px auto",
                                    backgroundColor: "white",
                                    position: "relative",
                                    boxSizing: "border-box",
                                    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",

                                    display: "flex",
                                    flexDirection: "column",

                                    // 3. Lock the overflow so it never pushes the page taller
                                    overflow: "hidden",
                                    pageBreakAfter: index === pages.length - 1 ? "auto" : "always",
                                }}
                            >
                                {/* Background Image */}

                                {letterTemplateData?.bg_display ?
                                    (
                                        letterTemplateData?.bg_image && (
                                            <div
                                                className="letter-template-bg-image-e"
                                                style={getBgImageStyle()}
                                            />
                                        )
                                    )
                                    :
                                    (

                                        subCompany?.company_logo && (
                                            <div
                                                className="letter-template-bg-image-e"
                                                style={{
                                                    backgroundImage: `url(${subCompany?.company_logo})`,
                                                    backgroundSize: "70%",
                                                    opacity: 0.2,
                                                }}
                                            />
                                        )
                                    )}

                                {/* Header */}

                                {(letterTemplateData?.header_logo || letterTemplateData?.header_company_name || letterTemplateData?.header_Address || letterTemplateData?.header_email || letterTemplateData?.header_full_image || subCompany?.company_logo || subCompany?.title || subCompany?.company_name || subCompany?.company_address) ? (
                                    <div
                                        className="letter-template-header-e"
                                        ref={headerRef}
                                        style={{
                                            position: "relative",
                                            zIndex: 1,
                                            paddingLeft: `${Number(letterTemplateData?.header_left_gap) || 20}px`,
                                            paddingRight: `${Number(letterTemplateData?.header_right_gap) || 20}px`,
                                            paddingTop: `${Number(letterTemplateData?.header_top_gap) || 20}px`,
                                            paddingBottom: `${Number(letterTemplateData?.header_bottom_gap) || 20}px`,

                                        }}
                                    >
                                        {(letterTemplateData?.header_logo || letterTemplateData?.header_company_name || letterTemplateData?.header_Address || letterTemplateData?.header_email || letterTemplateData?.header_full_image)
                                            ?
                                            (
                                                letterTemplateData?.header_full_image ? (
                                                    <div className='salaryslip-header'
                                                    >
                                                        <img
                                                            //  src={`${letterTemplate?.header_full_image}`} 
                                                            src={
                                                                letterTemplateData?.header_full_image instanceof File
                                                                    ? URL.createObjectURL(
                                                                        letterTemplateData?.header_full_image
                                                                    )
                                                                    : letterTemplateData?.header_full_image
                                                            }
                                                            alt='Header'
                                                            className="letter-template-header-full-image"

                                                        />
                                                    </div>
                                                )
                                                    :
                                                    (
                                                        (letterTemplateData?.header_logo || letterTemplateData?.header_company_name || letterTemplateData?.header_Address || letterTemplateData?.header_email) &&
                                                        (
                                                            <>
                                                                <div
                                                                    className="letter-template-footer-content"
                                                                    style={{
                                                                        display: "flex",
                                                                        alignItems: "center",
                                                                        justifyContent: getJustifyContent(letterTemplateData, "header"),
                                                                        // gap: `${letterTemplateData?.header_logo_position === "left" || letterTemplateData?.header_logo_position === "left" ? 10 : 0}px`,
                                                                        gap: (letterTemplateData?.header_logo_position === "right" &&
                                                                            letterTemplateData?.header_content_position === "right") ||
                                                                            (letterTemplateData?.header_logo_position === "left" &&
                                                                                letterTemplateData?.header_content_position === "left") ? "5px" : '15px'
                                                                    }}
                                                                >
                                                                    {/* Logo */}

                                                                    <div
                                                                        className="letter-template-header-logo-container"
                                                                        style={{
                                                                            order:
                                                                                letterTemplateData?.header_logo_position === "left"
                                                                                    ? 0
                                                                                    : letterTemplateData?.header_logo_position ===
                                                                                        "right"
                                                                                        ? 2
                                                                                        : 1,
                                                                            display: "flex",
                                                                            justifyContent:
                                                                                letterTemplateData?.header_logo_position === "center"
                                                                                    ? "center"
                                                                                    : getJustifyContentMore(letterTemplateData, "header"),
                                                                            width:
                                                                                letterTemplateData?.header_logo_position ===
                                                                                    "center" ||
                                                                                    letterTemplateData?.header_content_position ===
                                                                                    "center"
                                                                                    ? "100%"
                                                                                    : "auto",

                                                                            flex: "0 0 auto",
                                                                        }}
                                                                    >
                                                                        {letterTemplateData?.header_logo && (
                                                                            <img
                                                                                src={
                                                                                    letterTemplateData?.header_logo instanceof File
                                                                                        ? URL.createObjectURL(
                                                                                            letterTemplateData?.header_logo
                                                                                        )
                                                                                        : letterTemplateData?.header_logo
                                                                                }
                                                                                alt="Logo"
                                                                                className="letter-template-header-logo"

                                                                                style={{
                                                                                    width: `${letterTemplateData?.header_logo_size || 100}%`,
                                                                                    height: "auto",
                                                                                    objectFit: "contain",
                                                                                }}
                                                                            />
                                                                        )}
                                                                    </div>


                                                                    {/* Content */}
                                                                    <div
                                                                        className="letter-template-footer-text"
                                                                        style={{
                                                                            order:
                                                                                letterTemplateData?.header_content_position === "left"
                                                                                    ? 0
                                                                                    : letterTemplateData?.header_content_position ===
                                                                                        "right"
                                                                                        ? 2
                                                                                        : 1,

                                                                            width:
                                                                                letterTemplateData?.header_logo_position === "right" &&
                                                                                    letterTemplateData?.header_content_position === "center"
                                                                                    ? "auto"
                                                                                    : letterTemplateData?.header_logo_position === "center" ||
                                                                                        letterTemplateData?.header_content_position === "center"
                                                                                        ? "auto"
                                                                                        : letterTemplateData?.header_logo_position === "right" ||
                                                                                            letterTemplateData?.header_content_position === "right"
                                                                                            ? "auto"
                                                                                            : "100%",
                                                                            // flex:
                                                                            //     letterTemplateData?.header_logo_position === "right" &&
                                                                            //         letterTemplateData?.header_content_position === "center"
                                                                            //         ? "0 0 auto" :
                                                                            //         letterTemplateData?.header_logo_position === "center" ||
                                                                            //             letterTemplateData?.header_content_position === "center"
                                                                            //             ? "0 0 auto"
                                                                            //             : "0 0 auto",
                                                                            maxWidth: letterTemplateData?.header_content_position === "left" ? "230px"

                                                                                : letterTemplateData?.header_content_position === "right" ? "320px"
                                                                                    : 'auto',
                                                                            flex:
                                                                                // ✅ FIRST: both right (your requirement)
                                                                                letterTemplateData?.header_logo_position === "right" &&
                                                                                    letterTemplateData?.header_content_position === "right"
                                                                                    ? "none"

                                                                                    // existing logic continues
                                                                                    : letterTemplateData?.header_logo_position === "right" &&
                                                                                        letterTemplateData?.header_content_position === "center"
                                                                                        ? "0 0 auto"

                                                                                        : letterTemplateData?.header_logo_position === "center"
                                                                                            // ||
                                                                                            //     letterTemplateData?.header_content_position === "center"
                                                                                            ? "0 0 auto"

                                                                                            : letterTemplateData?.header_logo_position === "left" ||
                                                                                                letterTemplateData?.header_content_position === "right"
                                                                                                ? "1 1 0%"

                                                                                                : letterTemplateData?.header_logo_position === "right" ||
                                                                                                    letterTemplateData?.header_content_position === "left"
                                                                                                    ? "1 1 0%"
                                                                                                    : (letterTemplateData?.header_logo_position === "left" &&
                                                                                                        letterTemplateData?.header_content_position === "center")
                                                                                                        ? "1 1 0%"
                                                                                                        : "0 0 auto",
                                                                            // flex: "1 1 0%",
                                                                            minWidth: 0,
                                                                            textAlign:
                                                                                letterTemplateData?.header_content_position === "right"
                                                                                    ? "right"
                                                                                    : letterTemplateData?.header_content_position ===
                                                                                        "center"
                                                                                        ? "center"
                                                                                        : "left",
                                                                            display: "flex",
                                                                            flexDirection: "column",
                                                                            alignItems:
                                                                                letterTemplateData?.header_content_position === "right"
                                                                                    ? "flex-end"
                                                                                    : letterTemplateData?.header_content_position ===
                                                                                        "center"
                                                                                        ? "center"
                                                                                        : "flex-start",

                                                                            justifyContent:
                                                                                letterTemplateData?.header_content_position === "center"
                                                                                    ? "end"
                                                                                    : getJustifyContentMore(letterTemplateData, "header"),
                                                                        }}
                                                                    >
                                                                        {letterTemplateData?.header_company_name && (
                                                                            <h3
                                                                                className="letter-template-header-company"
                                                                                style={{
                                                                                    color:
                                                                                        letterTemplateData?.header_primary_colour ||
                                                                                        "black",
                                                                                }}
                                                                            >
                                                                                {letterTemplateData?.header_company_name}
                                                                            </h3>
                                                                        )}
                                                                        {letterTemplateData?.header_Address && (
                                                                            <div
                                                                                className="letter-template-header-address"

                                                                                style={{
                                                                                    width:
                                                                                        letterTemplateData?.header_logo_position === "center" || letterTemplateData?.header_logo_position === "right"
                                                                                            ? "auto"
                                                                                            : "100%",
                                                                                    color:
                                                                                        letterTemplateData?.header_secondery_colour || "black",
                                                                                }}
                                                                            >
                                                                                {renderDangerousHTML(letterTemplateData?.header_Address)}
                                                                            </div>
                                                                        )}
                                                                        {letterTemplateData?.header_email && (
                                                                            <div style={{
                                                                                width: letterTemplateData?.header_logo_position === "center"
                                                                                    || letterTemplateData?.header_logo_position === "right"
                                                                                    ? "auto"
                                                                                    : "100%",
                                                                            }}>
                                                                                <p
                                                                                    className="letter-template-header-email"
                                                                                    style={{
                                                                                        color:
                                                                                            letterTemplateData?.header_secondery_colour ||
                                                                                            "black",
                                                                                    }}
                                                                                >
                                                                                    {letterTemplateData?.header_email}
                                                                                </p>
                                                                            </div>
                                                                        )}
                                                                    </div>

                                                                </div>
                                                                {/* {!letterTemplateData?.header_full_image && letterTemplateData?.header_display && (<div className='salaryslip-hr mb-2'></div>)} */}
                                                            </>
                                                        )
                                                    )
                                            ) : (
                                                <>
                                                    <div className='salaryslip-header'>
                                                        {subCompany?.company_logo && (
                                                            <div className='salaryslip-header-left'>
                                                                <img src={subCompany?.company_logo} alt={subCompany?.title} className='salaryslip-logo' />
                                                            </div>
                                                        )}
                                                        <div className='salaryslip-header-right'>
                                                            <div className='salaryslip-header-right-top-red font-weight600 font-size-heading'>
                                                                {subCompany?.company_name}
                                                            </div>
                                                            <div className='salaryslip-header-right-bottom  font-weight400  font-size-subheading'>
                                                                {subCompany?.company_address && (
                                                                    <div className='slip-top-right-content' style={{ textAlign: 'right' }} dangerouslySetInnerHTML={{ __html: subCompany?.company_address }} />
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    {(subCompany && !letterTemplateData?.header_display) && <div className='salaryslip-hr'></div>}
                                                </>
                                            )
                                        }
                                    </div>
                                )
                                    : null
                                }

                                {/*------------------------------------------- body */}
                                <div
                                    className="letter-template-body-e-e"
                                    style={{
                                        paddingLeft: layout.paddingLeft + "px",
                                        paddingRight: layout.paddingRight + "px",
                                        paddingTop: layout.paddingTop + "px",
                                        paddingBottom: layout.paddingBottom + (!letterTemplateData?.footer_display ? 10 : 0) + 5 + "px",

                                        position: "relative",
                                        zIndex: 1,
                                        border: ((!content || content.trim() === "") || contentBorderDisplay)
                                            ? "4px dashed #e6e4e4ff"
                                            : "none",
                                        lineHeight: lineHeight ? lineHeight : "1.5",

                                        // Let it fill the remaining space exactly
                                        flex: "1 1 auto",

                                        // CRITICAL: Prevent the text from expanding and pushing the footer out
                                        overflow: "hidden",
                                    }}
                                >
                                    <div
                                        className="letter-template-content-placeholder-e"
                                        style={{
                                            // fontSize: `${fontSize}px`,
                                            lineHeight: lineHeight ? lineHeight : "1.5",
                                            boxSizing: "border-box",
                                            overflow: "visible",
                                            height: "100%",
                                        }}
                                        dangerouslySetInnerHTML={{
                                            __html: content
                                        }}
                                    />
                                    {/* {(letterTemplateData?.footer_display && !letterTemplateData?.footer_full_image) && (<div className='salaryslip-hr mt-4'></div>)} */}
                                </div>

                                {/* Footer */}

                                <div
                                    className="letter-template-footer-e"
                                    ref={footerRef}
                                    style={{
                                        paddingLeft: `${Number(letterTemplateData?.footer_left_gap) || 20}px`,
                                        paddingRight: `${Number(letterTemplateData?.footer_right_gap) || 20}px`,
                                        paddingTop: `${Number(letterTemplateData?.footer_top_gap) || 20}px`,
                                        paddingBottom: `${Number(letterTemplateData?.footer_bottom_gap) || 20}px`,
                                        position: "relative",
                                        bottom: 0,
                                        left: 0,
                                        right: 0,
                                        backgroundColor: letterTemplateData?.footer_display ? "white" : "transparent",
                                        zIndex: 10,
                                        marginTop: "auto",
                                        height: letterTemplateData?.footer_display ? "auto" : "0px",
                                        minHeight: letterTemplateData?.footer_display ? "auto" : "0px",
                                        overflow: letterTemplateData?.footer_display ? "visible" : "hidden",
                                    }}
                                >


                                    {letterTemplateData?.footer_display &&
                                        letterTemplateData?.footer_full_image ?
                                        (
                                            <img
                                                src={getImageSource(letterTemplateData?.footer_full_image)}
                                                alt="Footer"
                                                className="letter-template-footer-full-image"
                                            />
                                        ) :
                                        (letterTemplateData &&
                                            <div
                                                className="letter-template-footer-content"
                                                style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: getJustifyContent(letterTemplateData, "footer"),
                                                    gap: (letterTemplateData?.footer_logo_position === "right" &&
                                                        letterTemplateData?.footer_content_position === "right") ||
                                                        (letterTemplateData?.footer_logo_position === "left" &&
                                                            letterTemplateData?.footer_content_position === "left") ? "5px" : '15px'
                                                }}
                                            >
                                                <div
                                                    className="letter-template-header-logo-container"
                                                    style={{
                                                        order:
                                                            letterTemplateData?.footer_logo_position === "left"
                                                                ? 0
                                                                : letterTemplateData?.footer_logo_position === "right"
                                                                    ? 2
                                                                    : 1,

                                                        display: "flex",
                                                        justifyContent:
                                                            letterTemplateData?.footer_logo_position === "center"
                                                                ? "center"
                                                                : getJustifyContentMore(letterTemplateData, "footer"),
                                                        width:
                                                            letterTemplateData?.footer_logo_position === "center"
                                                                ||
                                                                letterTemplateData?.footer_content_position === "center"
                                                                ? "100%"
                                                                : "auto",

                                                        flex: "0 0 auto",
                                                    }}
                                                >
                                                    {letterTemplateData?.footer_logo && (
                                                        <img
                                                            src={getImageSource(letterTemplateData?.footer_logo)}
                                                            alt="Logo"
                                                            className="letter-template-header-logo"
                                                            style={{
                                                                width: `${letterTemplateData?.footer_logo_size || 100}%`,
                                                                height: "auto",
                                                                // maxWidth: "100%",
                                                                objectFit: "contain",
                                                            }}
                                                        />
                                                    )}
                                                </div>
                                                {/* )} */}
                                                <div
                                                    className="letter-template-footer-text"
                                                    style={{
                                                        order:
                                                            letterTemplateData?.footer_content_position === "left"
                                                                ? 0
                                                                : letterTemplateData?.footer_content_position ===
                                                                    "right"
                                                                    ? 2
                                                                    : 1,

                                                        width:
                                                            letterTemplateData?.footer_logo_position === "right" &&
                                                                letterTemplateData?.footer_content_position === "center"
                                                                ? "auto"
                                                                : letterTemplateData?.footer_logo_position === "center" ||
                                                                    letterTemplateData?.footer_content_position === "center"
                                                                    ? "auto"
                                                                    : letterTemplateData?.footer_logo_position === "right" ||
                                                                        letterTemplateData?.footer_content_position === "right"
                                                                        ? "auto"
                                                                        : "100%",
                                                        maxWidth: letterTemplateData?.footer_content_position === "left" ? "230px"

                                                            : letterTemplateData?.footer_content_position === "right" ? "320px"
                                                                : 'auto',
                                                        // flex: "1 1 0%",
                                                        flex:
                                                            // ✅ FIRST: both right (your requirement)
                                                            letterTemplateData?.footer_logo_position === "right" &&
                                                                letterTemplateData?.footer_content_position === "right"
                                                                ? "none"

                                                                // existing logic continues
                                                                : letterTemplateData?.footer_logo_position === "right" &&
                                                                    letterTemplateData?.footer_content_position === "center"
                                                                    ? "0 0 auto"

                                                                    : letterTemplateData?.footer_logo_position === "center"
                                                                        // ||
                                                                        //     letterTemplateData?.header_content_position === "center"
                                                                        ? "0 0 auto"

                                                                        : letterTemplateData?.footer_logo_position === "left" ||
                                                                            letterTemplateData?.footer_content_position === "right"
                                                                            ? "1 1 0%"

                                                                            : letterTemplateData?.footer_logo_position === "right" ||
                                                                                letterTemplateData?.footer_content_position === "left"
                                                                                ? "1 1 0%"
                                                                                : (letterTemplateData?.footer_logo_position === "left" &&
                                                                                    letterTemplateData?.footer_content_position === "center")
                                                                                    ? "1 1 0%"
                                                                                    : "0 0 auto",
                                                        minWidth: 0,


                                                        textAlign:
                                                            letterTemplateData?.footer_content_position === "right"
                                                                ? "right"
                                                                : letterTemplateData?.footer_content_position ===
                                                                    "center"
                                                                    ? "center"
                                                                    : "left",
                                                        display: "flex",
                                                        flexDirection: "column",
                                                        alignItems:
                                                            letterTemplateData?.footer_content_position === "right"
                                                                ? "flex-end"
                                                                : letterTemplateData?.footer_content_position ===
                                                                    "center"
                                                                    ? "center"
                                                                    : "flex-start",

                                                        justifyContent:
                                                            letterTemplateData?.footer_content_position === "center"
                                                                ? "end"
                                                                : getJustifyContentMore(letterTemplateData, "footer"),
                                                    }}
                                                >
                                                    {letterTemplateData?.footer_company_name && (
                                                        <h4
                                                            className="letter-template-header-company"
                                                            style={{
                                                                color:
                                                                    letterTemplateData?.footer_primary_colour || "black",
                                                            }}
                                                        >
                                                            {letterTemplateData?.footer_company_name}
                                                        </h4>
                                                    )}
                                                    {letterTemplateData?.footer_Address && (
                                                        <div
                                                            className="letter-template-footer-address"
                                                            style={{
                                                                width: letterTemplateData?.footer_logo_position === "center" || letterTemplateData?.header_logo_position === "right"
                                                                    ? "auto"
                                                                    : "100%",
                                                                // minWidth: 0,
                                                                color:
                                                                    letterTemplateData?.footer_secondery_colour ||
                                                                    "black",
                                                                // wordBreak: "break-word",
                                                                // overflowWrap: "break-word",
                                                                // whiteSpace: "normal",
                                                            }}
                                                        >
                                                            {renderDangerousHTML(letterTemplateData?.footer_Address)}
                                                        </div>
                                                    )}
                                                    {letterTemplateData?.footer_email && (
                                                        <div style={{
                                                            width: letterTemplateData?.footer_logo_position === "center"
                                                                || letterTemplateData?.footer_logo_position === "right"
                                                                ? "auto"
                                                                : "100%",
                                                        }}>
                                                            <p
                                                                className="letter-template-header-email"
                                                                style={{
                                                                    color:
                                                                        letterTemplateData?.footer_secondery_colour ||
                                                                        "black",
                                                                }}
                                                            >
                                                                {letterTemplateData?.footer_email}
                                                            </p>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        )
                                    }
                                </div>
                            </div>
                        )
                    })
                )}
            </div>
        </>
    )

}
)

const SignatureBlock = ({
    letterTemplateData,
    showDefaultSignText = false,
    letterData,
    subCompany
}) => {

    const hasLetterSignature =
        letterData?.authority_signatory && letterData?.authority_signature;

    const hasTemplateSignature = letterTemplateData?.signatory_display === true;

    // Priority 1: Letter Signature
    if (hasLetterSignature) {
        return (
            <>
                <b>
                    For{" "}
                    {subCompany?.company_name
                        ? subCompany?.company_name
                        : letterTemplateData?.sub_company_title}
                </b>

                <div>
                    <img
                        src={`${IMAGE_URL}${letterData?.authority_signature}`}
                        alt="signature"
                        className="slaryslip-bg-signature"
                    />
                </div>

                <b>AUTHORISED SIGNATORY</b>
            </>
        );
    }

    // Priority 2: Template Signature
    if (hasTemplateSignature) {
        return (
            <div
                style={{
                    display: "flex",
                    justifyContent:
                        letterTemplateData?.signatory_overall_position === "center"
                            ? "center"
                            : letterTemplateData?.signatory_overall_position === "right"
                                ? "flex-end"
                                : "flex-start",
                    width: "100%"
                }}
            >
                <div
                    className="letter-template-signatory"
                    style={{
                        textAlign:
                            letterTemplateData?.signatory_content_position === "center"
                                ? "center"
                                : letterTemplateData?.signatory_content_position === "right"
                                    ? "right"
                                    : "left"
                    }}
                >
                    {letterTemplateData?.signatory_sign_heading && (
                        <p className="font-weight600">
                            {letterTemplateData.signatory_sign_heading}
                        </p>
                    )}

                    {letterTemplateData?.signatory_signature && (
                        <img
                            src={
                                letterTemplateData.signatory_signature instanceof File
                                    ? URL.createObjectURL(
                                        letterTemplateData.signatory_signature
                                    )
                                    : letterTemplateData.signatory_signature
                            }
                            alt="Signature"
                            style={{
                                height: `${letterTemplateData.signatory_sign_size || 60}px`,
                                display: "block",
                                margin:
                                    letterTemplateData?.signatory_sign_position === "center"
                                        ? "10px auto"
                                        : letterTemplateData?.signatory_sign_position === "right"
                                            ? "10px 0 10px auto"
                                            : "10px auto 10px 0"
                            }}
                        />
                    )}

                    {letterTemplateData?.signatory_name && (
                        <p className="font-weight600" style={{ marginBottom: "3px" }}>
                            {letterTemplateData.signatory_name}
                        </p>
                    )}

                    {letterTemplateData?.signatory_designation && (
                        <p className="font-weight600">{letterTemplateData.signatory_designation}</p>
                    )}
                </div>
            </div>
        );
    }

    // Priority 3: Default Text
    if (showDefaultSignText) {
        return (
            <h6 style={{ fontSize: "10px", marginTop: "10px" }}>
                <b>*This is Software generated Letter no signature required*</b>
            </h6>
        );
    }

    // Priority 4: Nothing
    return null;
};
export { LetterPreview, SignatureBlock }














